import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FolderKanban, Database, Bot, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ConfirmDelete from "@/components/ConfirmDelete";
import RecentDocuments from "@/admin/RecentDocuments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [reindexing, setReindexing] = useState(false);

  const reindex = async () => {
    setReindexing(true);
    try {
      const r = await api.reindexStart();
      if (r.status === "already_running") toast.info("Yeniden indeksleme zaten çalışıyor...");
      else toast.info(`Yeniden indeksleme başladı (${r.total || 0} parça)...`);
      const poll = setInterval(async () => {
        try {
          const s = await api.reindexStatus();
          if (!s.running) {
            clearInterval(poll);
            setReindexing(false);
            if (s.error) toast.error("Yeniden indeksleme hatası: " + s.error);
            else toast.success(`Yeniden indeksleme tamamlandı (${s.done}/${s.total} parça)`);
          }
        } catch { /* geçici hata, tekrar denenecek */ }
      }, 3000);
    } catch {
      setReindexing(false);
      toast.error("Yeniden indeksleme başlatılamadı");
    }
  };

  const load = () => {
    setLoading(true);
    api.getProjects().then((d) => setProjects(d)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Proje adı gerekli");
    setSaving(true);
    try {
      const p = await api.createProject(form);
      toast.success("Proje oluşturuldu");
      setOpen(false);
      setForm({ name: "", description: "" });
      navigate(`/projects/${p.id}`);
    } catch {
      toast.error("Oluşturulamadı");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteProject(id);
      toast.success("Proje silindi");
      load();
    } catch {
      toast.error("Silinemedi");
    }
  };

  return (
    <div className="fade-up">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Çalışma alanı
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">Projeler</h1>
          <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
            Dokümanlarınızı projelere ve veri setlerine ayırın, kaynaklı cevap veren asistanlar
            oluşturup yayınlayın.
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <Button data-testid="reindex-btn" variant="outline" size="lg" className="rounded-full"
            onClick={reindex} disabled={reindexing} title="Tüm dokümanları arama motoruna yeniden indeksler">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${reindexing ? "animate-spin" : ""}`} />
            {reindexing ? "İndeksleniyor..." : "Yeniden İndeksle"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-project-btn" size="lg" className="rounded-full">
              <Plus className="mr-1.5 h-4 w-4" /> Yeni proje
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-project-dialog">
            <DialogHeader><DialogTitle>Yeni proje</DialogTitle></DialogHeader>
            <DialogDescription className="sr-only">Yeni bir proje oluşturun</DialogDescription>
            <div className="space-y-4 py-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium">Proje adı</label>
                <Input
                  data-testid="project-name-input"
                  placeholder="Örn. İnsan Kaynakları"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Açıklama</label>
                <Textarea
                  data-testid="project-desc-input"
                  placeholder="Kısa açıklama (opsiyonel)"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="project-save-btn" onClick={create} disabled={saving}>
                {saving ? "Oluşturuluyor..." : "Oluştur"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 animate-pulse rounded-2xl bg-secondary" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="soft-card flex flex-col items-center justify-center gap-3 px-6 py-20 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary">
              <FolderKanban className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-bold">Henüz proje yok</h3>
            <p className="max-w-sm text-sm text-muted-foreground">
              İlk projenizi oluşturun ve dokümanlarınızı yüklemeye başlayın.
            </p>
            <Button data-testid="empty-new-project-btn" className="mt-2 rounded-full" onClick={() => setOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> Proje oluştur
            </Button>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p, i) => (
              <div
                key={p.id}
                data-testid={`project-card-${p.id}`}
                onClick={() => navigate(`/projects/${p.id}`)}
                style={{ animationDelay: `${i * 60}ms` }}
                className="soft-card soft-card-hover fade-up group relative flex cursor-pointer flex-col p-5 text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-foreground text-background">
                    <FolderKanban className="h-5 w-5" />
                  </div>
                  <ConfirmDelete
                    testId={`delete-project-dialog-${p.id}`}
                    title="Projeyi sil?"
                    description={`"${p.name}" projesi ve altındaki tüm veri setleri, dokümanlar ve asistanlar kalıcı olarak silinecek. Bu işlem geri alınamaz.`}
                    onConfirm={() => remove(p.id)}
                    trigger={
                      <button
                        data-testid={`delete-project-${p.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded-lg p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                        title="Projeyi sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    }
                  />
                </div>
                <h3 className="mt-4 text-lg font-bold tracking-tight">{p.name}</h3>
                <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm text-muted-foreground">
                  {p.description || "Açıklama eklenmemiş"}
                </p>
                <div className="mt-4 flex items-center gap-4 border-t border-border pt-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Database className="h-3.5 w-3.5" /> Veri setleri</span>
                  <span className="flex items-center gap-1.5"><Bot className="h-3.5 w-3.5" /> Asistanlar</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {!loading && projects.length > 0 && <RecentDocuments />}
    </div>
  );
}
